import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import PaperliveLogo from "../../Header/paperlive_only_p.png";
import { EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT } from "../../constants/AzureDevops";

const StickySectionHeaderV3 = ({ scrollToSection, course }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));


  const sections = course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT ?
  [
    { id: "reviews", label: "Reviews" },
    { id: "curriculum", label: "Bootcamp Curriculum" },
    { id: "certifications", label: "Certifications" },
    { id: "youtube", label: "Our Success Stories" },
    { id: "pricing", label: "Bootcamp Fee" },
    { id: "batch", label: "Batch Details" },
  ]
  :
  [
    { id: "devops-highlight", label: "Bootcamp Highlight" },
    { id: "curriculum", label: "Bootcamp Curriculum" },
    { id: "pricing", label: "Bootcamp Fee" },
    { id: "batch", label: "Batch Details" },
    { id: "instructors", label: "Our Instructors" },
    { id: "youtube", label: "Our Success Stories" },
    { id: "career-assistance", label: "Career Assistance" },
    { id: "certifications", label: "Certifications" },
    { id: "reviews", label: "Reviews" },
  ];

  const [activeSection, setActiveSection] = useState(null);
  const [showSticky, setShowSticky] = useState(false);
  const scrollContainerRef = useRef(null);
  const tabRefs = useRef([]);
  const programmaticScroll = useRef(false);

  // Observe HeroSection to toggle sticky header
  useEffect(() => {
    const hero = document.getElementById("hero");
    if (!hero) return;

    const heroObserver = new IntersectionObserver(
      ([entry]) => setShowSticky(!entry.isIntersecting),
      { threshold: 0 }
    );

    heroObserver.observe(hero);
    return () => heroObserver.disconnect();
  }, []);

  // Observe sections to update active tab.
  // The section is "active" when it overlaps the middle band of the viewport,
  // so it also works for sections taller than the screen.
  // Sections can mount late (lazy/Suspense chunks), so keep retrying until
  // every id has been found and observed.
  useEffect(() => {
    // ids currently overlapping the band; when several overlap (short
    // sections), the topmost one in the document wins
    const intersecting = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) intersecting.add(entry.target.id);
          else intersecting.delete(entry.target.id);
        });

        if (programmaticScroll.current || intersecting.size === 0) return;

        const topmost = [...intersecting].sort((a, b) => {
          const ea = document.getElementById(a);
          const eb = document.getElementById(b);
          if (!ea || !eb) return 0;
          return ea.compareDocumentPosition(eb) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        })[0];
        setActiveSection(topmost);
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: 0 }
    );

    let cancelled = false;
    let retryTimer;
    let attempts = 0;
    const pending = new Set(sections.map((s) => s.id));

    const observeAvailableSections = () => {
      if (cancelled) return;
      pending.forEach((id) => {
        const el = document.getElementById(id);
        if (el) {
          observer.observe(el);
          pending.delete(id);
        }
      });
      // give lazy sections up to 30s to mount; ids that never mount are skipped
      if (pending.size > 0 && ++attempts < 30) {
        retryTimer = setTimeout(observeAvailableSections, 1000);
      }
    };
    observeAvailableSections();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      observer.disconnect();
    };
  }, []);

  // Smoothly scroll active tab into view on mobile
  useEffect(() => {
    if (!isMobile || !activeSection) return;

    const index = sections.findIndex((s) => s.id === activeSection);
    const tabEl = tabRefs.current[index];
    const containerEl = scrollContainerRef.current;

    if (tabEl && containerEl) {
      const tabLeft = tabEl.offsetLeft;
      const tabRight = tabLeft + tabEl.offsetWidth;
      const containerLeft = containerEl.scrollLeft;
      const containerRight =
        containerEl.scrollLeft + containerEl.offsetWidth;

      if (tabLeft < containerLeft || tabRight > containerRight) {
        requestAnimationFrame(() => {
          containerEl.scrollTo({
            left:
              tabLeft -
              containerEl.offsetWidth / 2 +
              tabEl.offsetWidth / 2,
            behavior: "smooth",
          });
        });
      }
    }
  }, [activeSection, isMobile]);

  // CLICK HANDLER (fixed highlight)
  const handleTabClick = (id) => {
    if (!id) return;

    programmaticScroll.current = true;

    // highlight immediately
    setActiveSection(id);

    scrollToSection(id);

    setTimeout(() => {
      programmaticScroll.current = false;
    }, 700); // match your scroll duration
  };

  if (!showSticky) return null;

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        backgroundColor: "#fff",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        gap: 2,
        borderBottom: "1px solid #e0e0e0",
        padding: "8px 12px",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      {/* Fixed Logo */}
      <Box sx={{ flexShrink: 0, mr: 2 }} onClick={() => !(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) &&(window.location.href = "/")}>
        <img src={PaperliveLogo} alt="Paperlive Logo" style={{ height: 32 }} loading="lazy" />
      </Box>

      {/* Scrollable Tabs */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          gap: isMobile ? 2 : 3,
          overflowX: "auto",
          flex: 1,
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          position: "relative",
        }}
      >
        {sections.map((section, index) => (
          <Typography
            key={section.id}
            ref={(el) => (tabRefs.current[index] = el)}
            onClick={() => handleTabClick(section.id)}
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: activeSection === section.id ? 600 : 400,
              cursor: "pointer",
              position: "relative",
              pb: 0.5,
              color: "#333",
              fontSize: isMobile ? "0.875rem" : "1rem",
              whiteSpace: "nowrap",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.05)",
                color: "#8C00FF",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                height: 3,
                width: activeSection === section.id ? "100%" : 0,
                backgroundColor: "#8C00FF",
                borderRadius: "2px",
                transition: "width 0.3s ease",
              },
            }}
          >
            {section.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default StickySectionHeaderV3;